import { z } from "zod";
import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { FormActionsNew } from "@/features/FormCard/Actions";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { useEditForm } from "@/features/Edit/hooks";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  strategy_from_id: z.coerce.number().nullable().optional(),
  strategy_to_id: z.coerce.number().nullable().optional(),
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),
  quantity: z.coerce.number().nullable().optional(),
  weight: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  exposition_local_ccy: z.coerce.number().nullable().optional(),
  exposition_ptf_ccy: z.coerce.number().nullable().optional(),
  time_serie_file_id: z.coerce.number().nullable().optional(),
});
const formSchemaFields = FormSchema.keyof().enum;

const client = Clients.C3;
const table_name = Tables.C3_DEDICATED_FUNDS;

export function C3DedicatedFundsNew() {
  const { t } = useTranslation([Tables.C3_DEDICATED_FUNDS, "tables"]);
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({ client, table_name, FormSchema, isNew: true });

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("dedicated_funds.new_dedicated_fund", {
                  defaultValue: "Nouveau fonds dédié",
                })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_from_id}
                    label={t(`columns.strategy_from_id`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Stratégie (depuis)",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_to_id}
                    label={t(`columns.strategy_to_id`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Stratégie (vers)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`columns.date`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.quantity}
                    label={t(`columns.quantity`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Quantité",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.weight}
                    label={t(`columns.weight`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Poids",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_local_ccy}
                    label={t(`columns.market_value_local_ccy`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Valeur marché (devise locale)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_ptf_ccy}
                    label={t(`columns.market_value_ptf_ccy`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Valeur marché (devise ptf)",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.exposition_local_ccy}
                    label={t(`columns.exposition_local_ccy`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Exposition (devise locale)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.exposition_ptf_ccy}
                    label={t(`columns.exposition_ptf_ccy`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Exposition (devise ptf)",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.time_serie_file_id}
                    label={t(`columns.time_serie_file_id`, {
                      ns: Tables.C3_DEDICATED_FUNDS,
                      defaultValue: "Fichier de série temporelle",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
        >
          {changedFields
            ? NewDiff(changedFields, [], fieldTranslations, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
